import type { BoardTile } from '../../../engine/board.js';

/**
 * Turtle layout — the canonical 144-tile mahjong solitaire shape.
 *
 * Coordinates and row-widths copied from KMahjongg's `default.layout`
 * (https://github.com/KDE/kmahjongg/blob/master/layouts/default.layout) —
 * the same shape Microsoft Mahjong Titans / Activision Shanghai / GNOME
 * Mahjongg's "the_turtle" all use. Distinguishing features:
 *
 *   - Base layer is 8 rows with widths 12-8-10-12-12-10-8-12 (stepped, not
 *     elliptical). The 8-wide row right under the top edge is what gives
 *     the turtle its shell-plate silhouette.
 *   - Head is a single tile glued to the LEFT edge of the central row,
 *     tail is TWO tiles glued to the RIGHT edge — both on a half-cell row
 *     (y = 0 in centered coords) between body rows ±0.5.
 *   - Apex straddles the four layer-3 tiles on a half-cell offset in BOTH
 *     axes, so it visually sits dead-center on the 2×2 below it.
 *
 * Origin: shifted from KMahjongg's (0..7, -7.5..6.5) source coords so the
 * overall silhouette is roughly centered on (0, 0). The body itself is
 * one half-cell off-center in x — the head (1 tile) and tail (2 tiles)
 * balance the silhouette, but individual rows still sit at x ∈ {…, -0.5}.
 *
 *   Layer 0 (87) — body + head + tail
 *   Layer 1 (36) — 6×6 block
 *   Layer 2 (16) — 4×4 block
 *   Layer 3  (4) — 2×2 block
 *   Layer 4  (1) — apex
 */

const ROW_BODY_12 = [-5.5, -4.5, -3.5, -2.5, -1.5, -0.5, 0.5, 1.5, 2.5, 3.5, 4.5, 5.5];
const ROW_BODY_8  = [-3.5, -2.5, -1.5, -0.5, 0.5, 1.5, 2.5, 3.5];
const ROW_BODY_10 = [-4.5, -3.5, -2.5, -1.5, -0.5, 0.5, 1.5, 2.5, 3.5, 4.5];

export const turtleLayout: ReadonlyArray<Omit<BoardTile, 'id'>> = (() => {
  const out: Omit<BoardTile, 'id'>[] = [];

  // ── Layer 0 (87 tiles) ─────────────────────────────────────────
  // 8 body rows centered on y = -3.5 .. 3.5, half-cell apart.
  // Wait — the body uses integer y rows; half-cell offsets come from x.
  // Rows in canonical coords are y = 0..7; centered we get y = -3.5..3.5.
  const bodyRows: Array<[number[], number]> = [
    [ROW_BODY_12, -3.5],
    [ROW_BODY_8,  -2.5],
    [ROW_BODY_10, -1.5],
    [ROW_BODY_12, -0.5],
    [ROW_BODY_12,  0.5],
    [ROW_BODY_10,  1.5],
    [ROW_BODY_8,   2.5],
    [ROW_BODY_12,  3.5],
  ];
  for (const [xs, y] of bodyRows) {
    for (const x of xs) out.push({ x, y, layer: 0 });
  }
  // Head (1 tile, left) and tail (2 tiles, right) on the centerline (y=0).
  // They glue directly to the edges of the y = ±0.5 rows (which span
  // x = -5.5 .. 5.5), so head sits one tile beyond the left edge and tail
  // sits two tiles beyond the right edge.
  out.push({ x: -6.5, y: 0, layer: 0 });   // head
  out.push({ x:  6.5, y: 0, layer: 0 });   // tail (inner)
  out.push({ x:  7.5, y: 0, layer: 0 });   // tail (outer)

  // ── Layer 1 (36 tiles) — 6×6 block above the central body ──────
  for (let yi = 0; yi < 6; yi++) {
    const y = -2.5 + yi;
    for (let xi = 0; xi < 6; xi++) {
      const x = -2.5 + xi;
      out.push({ x, y, layer: 1 });
    }
  }

  // ── Layer 2 (16 tiles) — 4×4 block ─────────────────────────────
  for (let yi = 0; yi < 4; yi++) {
    const y = -1.5 + yi;
    for (let xi = 0; xi < 4; xi++) {
      const x = -1.5 + xi;
      out.push({ x, y, layer: 2 });
    }
  }

  // ── Layer 3 (4 tiles) — 2×2 ────────────────────────────────────
  for (const y of [-0.5, 0.5]) {
    for (const x of [-0.5, 0.5]) {
      out.push({ x, y, layer: 3 });
    }
  }

  // ── Layer 4 (apex) — straddles the 2×2 below ───────────────────
  out.push({ x: 0, y: 0, layer: 4 });

  if (out.length !== 144) {
    throw new Error(`turtle layout has ${out.length} positions, expected 144`);
  }
  return out;
})();
